import React, { useState } from "react";
import { useFormikContext } from "formik";
import { IoIosCheckmarkCircle } from "@hacknug/react-icons/io";
import { COLORS } from "utils/styles";
import cloneDeep from "lodash/cloneDeep";
import classnames from "classnames";
import HoverIconButton from "components/HoverIconButton";
import CopyToClipboard from "components/CopyToClipboard";
import get from "lodash/get";
import pick from "lodash/pick";
import { STATE_KEYS } from "utils/constants/state";
import { setPrimaryPriceForInterval } from "utils/pricing";
import Tooltip from "components/Tooltip";
import { PRIMARY_PRICE_KEYS } from "utils/constants/price";
import { usePageBuilderContext } from "utils/context";
import { SELECT_PRIMARY_PRICE } from "utils/analytics";
import { getListBorders } from "utils/constants/ui";
import { useHoverProps } from "utils/hooks";
import { getCopyPriceIdProps, getStripeFormattedAmount } from "utils/price";
import MonthlyIntervalShorthand from "components/MonthlyIntervalShorthand";
import { getPriceTaxBehaviorLabel } from "utils/taxRate";

const COMPONENT = "PriceSelectOption";

const PriceSelectOption = ({ price, isFirst, isLast, selected, canSelect }) => {
  const { tracking } = usePageBuilderContext();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const [hover, setHover] = useState(false);
  const hoverProps = useHoverProps({ hover, setHover });
  const { active } = price;

  const taxBehavior = getPriceTaxBehaviorLabel(price);
  const hasFooter = price.nickname || taxBehavior;

  const actionProps = {
    size: 24,
    color: active
      ? selected
        ? COLORS.blue
        : COLORS.secondary
      : COLORS.whitesmoke
  };
  const choice = {
    product: price.product,
    price: price.id,
    active: active,
    currency: price.currency
  };
  if (price.recurring && price.recurring.interval) {
    choice.interval = price.recurring.interval;
  }
  const containerProps = {
    className: getListBorders({
      className:
        "flex flex-column items-stretch b--black-20 bl br pointer user-select-n w-100",
      isFirst,
      isLast
    })
  };

  const formattedAmount = getStripeFormattedAmount(price);

  const setPrimaryPrice = setPrimaryPriceForInterval({
    primaries: cloneDeep(get(values, STATE_KEYS.MANIFEST.PRICING_PRIMARIES)),
    choice: pick(choice, PRIMARY_PRICE_KEYS),
    stateKey: STATE_KEYS.MANIFEST.PRICING_PRIMARIES,
    setFieldValue,
    setFieldTouched,
    tracking: {
      event: SELECT_PRIMARY_PRICE,
      data: {
        ...choice,
        component: COMPONENT,
        ...tracking
      }
    }
  });

  return (
    <div {...containerProps}>
      <div className="w-100 flex flex-row items-stretch justify-between">
        <div className="flex flex-row w-100 items-stretch">
          <div className="h-100 items-center flex br b--black-20">
            <CopyToClipboard {...getCopyPriceIdProps(price)} />
          </div>
          <div
            onClick={active ? setPrimaryPrice : () => {}}
            className={classnames("flex flex-row w-100", {
              "bg-white": !hover,
              "bg-content--secondary": hover && active
            })}
            {...hoverProps}
          >
            <div
              style={{ width: 52 }}
              className="h-100 items-center flex br b--black-20"
            >
              <div className="f7 ttu fw5 ph2">{price.currency}</div>
            </div>
            <div className="flex flex-row items-center w-100 justify-between">
              <div className="f5 fw5 pa2">{formattedAmount}</div>
              <MonthlyIntervalShorthand price={price} />
            </div>
          </div>
        </div>
        <div className="flex flex-row flex-shrink-0">
          <div
            onClick={active ? setPrimaryPrice : () => {}}
            className={classnames("flex justify-center items-center pr2", {
              "bg-white": !hover,
              "bg-content--secondary": hover && active
            })}
          >
            <Tooltip
              id={price.id}
              tooltip={`${selected ? "Unset" : "Set"} primary price`}
            >
              <IoIosCheckmarkCircle {...actionProps} />
            </Tooltip>
          </div>
          <div className="h-100 items-center flex bl b--black-20">
            <HoverIconButton
              type="edit"
              tooltip="Edit"
              onClick={() => {
                setFieldValue(STATE_KEYS.EDITOR.PRICING_ACTIVE_PRICE, price.id);
              }}
            />
          </div>
        </div>
      </div>
      {hasFooter && (
        <div className="flex flex-row items-center w-100 justify-between bt hairline-1">
          {taxBehavior && <div className="f7 ph2 pv1">{taxBehavior}</div>}
          {price.nickname && <div className="f7 ph2 pv1">{price.nickname}</div>}
        </div>
      )}
    </div>
  );
};

export default PriceSelectOption;
